import { useState, useRef, useEffect, useMemo } from "react";
import { IonModal } from "@ionic/react";
import { Plus, Link2 } from "lucide-react";
import type { ITask } from "../types";
import { useStore } from "../store/useStore";
import { parseMultiLineInput } from "../lib/parse-input";

interface AddPrerequisiteModalProps {
  isOpen: boolean;
  onClose: () => void;
  dependentTask: ITask;
}

export default function AddPrerequisiteModal({
  isOpen,
  onClose,
  dependentTask,
}: AddPrerequisiteModalProps) {
  const [input, setInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const createPrerequisiteTask = useStore((s) => s.createPrerequisiteTask);

  useEffect(() => {
    if (!isOpen) {
      setInput("");
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const parsedItems = useMemo(() => parseMultiLineInput(input), [input]);
  const isMultiTask = parsedItems.length > 1;

  const handleSubmit = async () => {
    const trimmed = input.trim();
    if (!trimmed || isSubmitting) return;

    setIsSubmitting(true);

    if (isMultiTask) {
      // Create one prereq per parsed line, in order — all parallel prereqs of the same dependent
      for (const item of parsedItems) {
        await createPrerequisiteTask(item.text, dependentTask.uuid);
      }
    } else {
      await createPrerequisiteTask(trimmed, dependentTask.uuid);
    }

    setIsSubmitting(false);
    setInput("");
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      if (isMultiTask) return;
      e.preventDefault();
      handleSubmit();
    }
  };

  const parentTitle = dependentTask.title || dependentTask.raw_input;

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      initialBreakpoint={0.5}
      breakpoints={[0, 0.5, 0.85]}
      onDidPresent={() => textareaRef.current?.focus()}
    >
      <div className="p-5 space-y-3">
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-foreground">
            Add prerequisite
          </h2>
          <div className="flex items-center gap-1.5 text-xs text-primary">
            <Link2 className="w-3 h-3" />
            <span>Prerequisite for:</span>
            <span className="font-medium text-foreground truncate">
              {parentTitle}
            </span>
          </div>
        </div>

        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="What needs to be done first?"
          rows={3}
          className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />

        {isMultiTask && (
          <p className="text-xs text-muted-foreground">
            Creates {parsedItems.length} prerequisites, in order
          </p>
        )}

        <button
          onClick={handleSubmit}
          disabled={!input.trim() || isSubmitting}
          className="btn-primary w-full flex items-center justify-center gap-1.5"
        >
          <Plus className="w-4 h-4" />
          {isSubmitting
            ? "Adding..."
            : isMultiTask
              ? `Add ${parsedItems.length} prerequisites`
              : "Add prerequisite"}
        </button>
      </div>
    </IonModal>
  );
}
